import { useMutation } from '@tanstack/react-query';
import { useTranslate } from '@bynder/localization';
import {
  createGroup,
  createGuideWithTemplateInsideGroupApi,
  deleteGroup,
  editGroupCoverAsset,
  editGroupDescription,
  editGroupTitle,
} from '../helpers/api/guidelinesApi';
import { useOverviewGroupById } from '../queries/groups';
import { Cover, Group, GuideType } from '../types';
import { queryClient } from '../constants/app';
import { OVERVIEW_KEYS } from '../queries/guides';
import {
  getOrderByFromLocalStorage,
  getOrderTypeFromLocalStorage,
} from '../helpers/browser';
import { generateUUID } from '../helpers/utils';
import { handleMutationSuccess } from './helpers';
import { useModalStore } from '../stores/modalStore';
import { GroupAttributes } from '../components/overview/v2/Overview/OverviewHeaderV2';
import { useAssignThemesetToGuide } from './themesets';

export const useCreateGroup = () => {
  const groupId = generateUUID();
  const groupVersion = 0;
  const toggleIsCreatingGroup = useModalStore.use.toggleIsCreatingGroup();

  return useMutation(
    (createGroupOptions: GroupAttributes) => {
      return createGroup(
        groupId,
        groupVersion,
        createGroupOptions.groupTitle,
        createGroupOptions.groupDescription || '',
      );
    },
    {
      onMutate: () => {
        toggleIsCreatingGroup(true);
      },
      onSuccess: data => {
        handleMutationSuccess('Group Created', data, {
          entityId: groupId,
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
        });
      },
    },
  );
};

interface EditGroupTitleOptions {
  groupTitle: string;
}

export const useEditGroupTitle = (groupId: string) => {
  const group = useOverviewGroupById(groupId);
  const { translate } = useTranslate();

  return useMutation(
    (editGroupTitleOptions: EditGroupTitleOptions) => {
      return editGroupTitle(
        groupId,
        group.version,
        editGroupTitleOptions.groupTitle,
      );
    },
    {
      onSuccess: (data, variables: EditGroupTitleOptions) => {
        optimisticallyUpdateGroupTitle(groupId, variables.groupTitle);
        handleMutationSuccess('Group Edited', data, {
          entityId: groupId,
          message: translate('OVERVIEW_CARD_MUTATION_MESSAGE.RENAMING_GROUP'),
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
        });
      },
    },
  );
};

interface EditGroupDescriptionOptions {
  groupDescription: string;
}

export const useEditGroupDescription = (groupId: string) => {
  const group = useOverviewGroupById(groupId);
  const { translate } = useTranslate();

  return useMutation(
    (editGroupDescriptionOptions: EditGroupDescriptionOptions) => {
      return editGroupDescription(
        groupId,
        group.version,
        editGroupDescriptionOptions.groupDescription,
      );
    },
    {
      onSuccess: (data, variables: EditGroupDescriptionOptions) => {
        optimisticallyUpdateGroupDescription(
          groupId,
          variables.groupDescription,
        );
        handleMutationSuccess('Group Edited', data, {
          entityId: groupId,
          message: translate(
            'OVERVIEW_CARD_MUTATION_MESSAGE.EDITING_GROUP_DESCRIPTION',
          ),
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
        });
      },
    },
  );
};

interface SetGroupCoverOptions {
  groupCover: Cover;
}

export const useSetGroupCover = (groupId: string) => {
  const group = useOverviewGroupById(groupId);
  const { translate } = useTranslate();

  return useMutation(
    (setGroupCoverOptions: SetGroupCoverOptions) => {
      return editGroupCoverAsset(
        groupId,
        group.version,
        setGroupCoverOptions.groupCover,
      );
    },
    {
      onSuccess: data => {
        handleMutationSuccess('Group Edited', data, {
          entityId: groupId,
          message: translate('OVERVIEW_CARD_MUTATION_MESSAGE.SET_COVER'),
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
        });
      },
    },
  );
};

export const useDeleteGroup = (groupId: string) => {
  const group = useOverviewGroupById(groupId);
  const { translate } = useTranslate();

  return useMutation(
    () => {
      return deleteGroup(groupId, group.version);
    },
    {
      onSuccess: data => {
        handleMutationSuccess('Group Deleted', data, {
          entityId: groupId,
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
          message: translate('OVERVIEW_CARD_MUTATION_MESSAGE.DELETING_GROUP'),
        });
      },
    },
  );
};

interface CreateGuideInGroupOptions {
  guideTitle: string;
  themesetId?: string;
}

export const useCreateGuideInsideGroup = (groupId: string) => {
  const { translate } = useTranslate();
  const chapterTitle = translate('GUIDE.UNTITLED_CHAPTER');
  const pageTitle = translate('GUIDE.UNTITLED_PAGE');
  const guideId = generateUUID();
  const chapterId = generateUUID();
  const pageId = generateUUID();
  const sectionId = generateUUID();
  const guideVersion = 0;
  const group = useOverviewGroupById(groupId);
  const toggleIsCreatingGuide = useModalStore.use.toggleIsCreatingGuide();
  const assignThemesetToGuideMutation = useAssignThemesetToGuide();

  return useMutation(
    (createGuideInGroupOptions: CreateGuideInGroupOptions) =>
      createGuideWithTemplateInsideGroupApi(
        guideId,
        createGuideInGroupOptions.guideTitle,
        chapterId,
        chapterTitle,
        pageId,
        pageTitle,
        sectionId,
        group.id,
        group.version,
        guideVersion,
        GuideType.MULTI_PAGE,
      ),
    {
      onMutate: () => {
        toggleIsCreatingGuide(true);
      },
      onSuccess: (data, variables) => {
        if (variables.themesetId) {
          assignThemesetToGuideMutation.mutate({
            guideId,
            themesetId: variables.themesetId,
          });
        }
        handleMutationSuccess('Guide Created', data, {
          entityId: guideId,
          queryPath: OVERVIEW_KEYS.OVERVIEW_GROUPS,
        });
      },
    },
  );
};

const optimisticallyUpdateGroupTitle = (
  groupId: string,
  groupTitle: string,
) => {
  queryClient.setQueryData(
    [
      OVERVIEW_KEYS.OVERVIEW_GROUPS,
      getOrderByFromLocalStorage(),
      getOrderTypeFromLocalStorage(),
    ],
    (old: Group[]) =>
      old.map(group =>
        group.id === groupId ? { ...group, title: groupTitle } : group,
      ),
  );
};
const optimisticallyUpdateGroupDescription = (
  groupId: string,
  groupDescription: string,
) => {
  queryClient.setQueryData(
    [
      OVERVIEW_KEYS.OVERVIEW_GROUPS,
      getOrderByFromLocalStorage(),
      getOrderTypeFromLocalStorage(),
    ],
    (old: Group[]) =>
      old.map(group =>
        group.id === groupId
          ? { ...group, description: groupDescription }
          : group,
      ),
  );
};
